import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, UserPlus, Pencil, KeyRound, Ban, ShieldCheck, Trash2 } from "lucide-react";

const ROLES = Object.keys(ROLE_LABELS);

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", confirm: "", role: "STAFF_OPERASIONAL" });
  const [edit, setEdit] = useState(null);
  const [reset, setReset] = useState(null);
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user?.role === "SUPER_ADMIN";

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.password !== form.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    try {
      await api.post("/users", { name: form.name, username: form.username || null, email: form.email, password: form.password, role: form.role });
      toast.success("Pengguna dibuat");
      setOpen(false); setForm({ name: "", username: "", email: "", password: "", confirm: "", role: "STAFF_OPERASIONAL" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/users/${edit.id}`, { name: edit.name, username: edit.username || null, email: edit.email, role: edit.role, is_active: edit.is_active });
      toast.success("Pengguna diperbarui"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doReset = async () => {
    if (pw.password !== pw.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    if (pw.password.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    try {
      await api.post(`/users/${reset.id}/reset-password`, { password: pw.password });
      toast.success("Password direset. Password lama tidak berlaku lagi."); setReset(null); setPw({ password: "", confirm: "" });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); toast.success(u.is_active ? "Dinonaktifkan" : "Diaktifkan"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const handleDeletePermanent = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}/permanent`);
      toast.success("Pengguna berhasil dihapus permanen");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">Manajemen Pengguna</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pengguna</h1>
          <p className="text-sm text-muted-foreground mt-1">Hanya Super Admin yang dapat mengelola pengguna.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full" data-testid="new-user-btn"><Plus className="h-4 w-4 mr-1.5" /> Pengguna Baru</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><UserPlus className="h-5 w-5" /> Buat Pengguna</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-4 py-2">
                <div className="space-y-2"><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="u-name" /></div>
                <div className="space-y-2"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="u-username" /></div>
                <div className="space-y-2 sm:col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="u-email" /></div>
                <div className="space-y-2"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="u-password" /></div>
                <div className="space-y-2"><Label>Konfirmasi Password</Label><Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="u-confirm" /></div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="u-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
                <Button onClick={create} disabled={!form.name || !form.email || !form.password} data-testid="u-submit">Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Nama</TableHead><TableHead>Username</TableHead><TableHead>Email</TableHead>
              <TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Login Terakhir</TableHead><TableHead>Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {!users ? (
                <TableRow><TableCell colSpan={7}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.username || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell><Badge variant="secondary">{ROLE_LABELS[u.role]}</Badge></TableCell>
                  <TableCell><span className={`text-xs font-medium ${u.is_active ? "text-emerald-600" : "text-red-500"}`}>{u.is_active ? "Aktif" : "Nonaktif"}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">{u.last_login ? u.last_login.slice(0, 16).replace("T", " ") : "—"}</TableCell>
                  <TableCell>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit({ ...u, username: u.username || "" })} data-testid={`user-edit-${u.email}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setReset(u); setPw({ password: "", confirm: "" }); }} data-testid={`user-reset-${u.email}`}><KeyRound className="h-3.5 w-3.5 text-amber-600" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleActive(u)} data-testid={`user-active-${u.email}`}>
                          {u.is_active ? <Ban className="h-3.5 w-3.5 text-red-500" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDeleteTarget(u)} data-testid={`user-delete-${u.email}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Edit Pengguna</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-4 py-2">
              <div className="space-y-2"><Label>Nama Lengkap</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="ue-name" /></div>
              <div className="space-y-2"><Label>Username</Label><Input value={edit.username} onChange={(e) => setEdit({ ...edit, username: e.target.value })} data-testid="ue-username" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>Email</Label><Input type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="ue-email" /></div>
              <div className="space-y-2"><Label>Role</Label>
                <Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v })}>
                  <SelectTrigger data-testid="ue-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={edit.is_active ? "1" : "0"} onValueChange={(v) => setEdit({ ...edit, is_active: v === "1" })}>
                  <SelectTrigger data-testid="ue-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="1">Aktif</SelectItem><SelectItem value="0">Nonaktif</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Batal</Button>
            <Button onClick={saveEdit} data-testid="ue-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!reset} onOpenChange={(o) => !o && setReset(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><KeyRound className="h-5 w-5" /> Reset Password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Atur password baru untuk <span className="font-semibold">{reset?.name}</span>. Password lama akan langsung tidak berlaku.</p>
          <div className="space-y-3 py-2">
            <div className="space-y-2"><Label>Password Baru</Label><Input type="password" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} data-testid="reset-password" /></div>
            <div className="space-y-2"><Label>Konfirmasi Password Baru</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="reset-confirm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReset(null)}>Batal</Button>
            <Button onClick={doReset} disabled={!pw.password} data-testid="reset-submit">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete permanent confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2 text-red-600"><Trash2 className="h-5 w-5" /> Hapus Pengguna Permanen</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Akun <span className="font-semibold text-foreground">{deleteTarget?.name}</span> ({deleteTarget?.username || deleteTarget?.email}) akan dihapus <span className="font-semibold text-red-600">permanen</span> dari database dan tidak dapat dikembalikan.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Batal</Button>
            <Button variant="destructive" onClick={handleDeletePermanent} disabled={deleting} data-testid="delete-permanent-submit">
              {deleting ? "Menghapus..." : "Ya, Hapus Permanen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
